import { useCallback, useMemo, useRef, useState } from 'react';
import { ApiError } from '../lib/http/ApiError';
import type { ApiErrorPayload, FetchClient, RequestOptions } from '../lib/http/http.types';
import { useAuth } from '../features/auth/hooks/useAuth';
import { DEFAULT_TENANT } from '../shared/constants';

const BASE_URL = `${window.__env__?.VITE_BACKEND_HOMERE_CONNECT_DOMAIN ?? '/api'}`;

async function handleResponse<T>(response: Response): Promise<T> {
  const text = await response.text();

  if (!response.ok) {
    const fallback: ApiErrorPayload = {
      message: text || response.statusText,
      statusCode: response.status,
    };
    const payload: ApiErrorPayload = (() => {
      try {
        const parsed: unknown = JSON.parse(text);
        // JSON valide mais pas un objet (null, nombre, tableau…) : traité comme un corps non-JSON.
        return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
          ? (parsed as ApiErrorPayload)
          : fallback;
      } catch {
        return fallback;
      }
    })();
    throw new ApiError(payload, response.status);
  }

  if (!text) {
    // handle empty response as undefined (e.g. DELETE) — avoids JSON.parse error
    return undefined as unknown as T;
  }
  return JSON.parse(text) as T;
}

/**
 * Hook HTTP authentifié — l'API wrapper central de l'application.
 *
 * Lit le token depuis AuthContext au moment de chaque envoi.
 * Sur HTTP 401, renouvelle le jeton (renewToken, POST /auth/refresh) puis rejoue la
 * requête une seule fois ; la session ne se termine que si le renouvellement est refusé
 * ou si la requête rejouée reçoit encore 401 (HOMCO-1269). Sur HTTP 403, resynchronise
 * le rôle et les droits via refreshPermissions().
 * Retourne un tuple [client, isLoading] :
 *   - client : interface FetchClient stable — elle ne change pas quand le jeton est
 *     renouvelé, pour ne pas relancer les effets des hooks de données (HOMCO-1269)
 *   - isLoading : true dès qu'au moins une requête est en cours (compteur inflight)
 *
 * Utilisation dans une feature hook :
 *   const [client, loading] = useFetch();
 *   const api = useMemo(() => createUsersApi(client), [client]);
 *
 * Migration TanStack Query (seule la couche hook change) :
 *   const [client] = useFetch();            // client reste identique
 *   return useQuery({ queryFn: ({ signal }) => client.get('/users', { signal }) });
 */

// TODO : Replace tenant-id header with domain-based tenant resolution when backend supports it (e.g. tenant1.homereconnect.fr → tenant-id = 'tenant1'). The tenant-id header is intentional for dev & test environments only and must be removed before going to production.
export function useFetch(): [FetchClient, boolean] {
  const { getToken, getSessionGeneration, renewToken, endSession, refreshPermissions } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const inflight = useRef(0);

  // Envoie la requête construite par `init` pour un jeton donné ; sur 401, renouvelle
  // le jeton puis rejoue une seule fois (HOMCO-1269). Le 401 vient du guard JWT, avant
  // tout traitement : rejouer est sans risque, même pour un POST.
  const execute = useCallback(
    async <T>(
      path: string,
      init: (token: string | null) => RequestInit,
      signal?: AbortSignal,
    ): Promise<T> => {
      inflight.current += 1;
      setIsLoading(true);

      try {
        const url = `${BASE_URL}${path}`;
        const token = getToken();
        const generation = getSessionGeneration();
        let response = await fetch(url, init(token));

        if (response.status === 401) {
          if (!token) {
            // Pas de jeton : rien à renouveler, la session est refusée (HOMCO-1184) — sauf
            // si une connexion a eu lieu depuis l'envoi : ce 401 ne la concerne pas.
            if (generation === getSessionGeneration()) endSession();
          } else {
            // Une requête annulée par son appelant n'est ni renouvelée ni rejouée : elle
            // échoue en AbortError, comme fetch, que les hooks de données ignorent (HOMCO-1269).
            signal?.throwIfAborted();
            let renewed = await renewToken(token, generation);
            if (renewed.kind === 'renewed') {
              signal?.throwIfAborted();
              response = await fetch(url, init(renewed.token));
              // Jeton adopté (renouvelé par un autre onglet) encore refusé : il a pu expirer
              // depuis. Un second renouvellement fait cette fois un vrai refresh.
              if (
                response.status === 401 &&
                renewed.adopted &&
                generation === getSessionGeneration()
              ) {
                renewed = await renewToken(renewed.token, generation);
                if (renewed.kind !== 'renewed') return await handleResponse<T>(response);
                signal?.throwIfAborted();
                response = await fetch(url, init(renewed.token));
              }
              // Jeton neuf encore refusé : la session est morte — sauf si elle a
              // changé pendant le rejeu (déconnexion, nouvelle connexion).
              if (response.status === 401 && generation === getSessionGeneration()) {
                endSession();
              }
            }
            // `refused` : renewToken a terminé la session (ou la requête appartenait à
            // une session précédente). `unavailable` : la session est conservée.
          }
        }

        // 403 : rôle ou droits peut-être modifiés depuis le chargement (le rôle est
        // relu en base à chaque requête côté API) — on resynchronise /auth/me, de sorte
        // que menus et ProtectedRoute se réévaluent sans rechargement.
        if (response.status === 403) {
          void refreshPermissions();
        }

        return await handleResponse<T>(response);
      } finally {
        inflight.current -= 1;
        if (inflight.current === 0) setIsLoading(false);
      }
    },
    [getToken, getSessionGeneration, renewToken, endSession, refreshPermissions],
  );

  const request = useCallback(
    <T>(method: string, path: string, body?: unknown, options?: RequestOptions): Promise<T> => {
      const isFormData = body instanceof FormData;
      return execute<T>(
        path,
        token => {
          const headers = new Headers({
            'Content-Type': 'application/json',
            ...options?.headers,
            'tenant-id': DEFAULT_TENANT,
          });

          // FormData : le navigateur fixe lui-même le Content-Type avec le boundary. `delete`
          // est insensible à la casse, contrairement au spread ci-dessus (HOMCO-1405).
          if (isFormData) {
            headers.delete('Content-Type');
          }

          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
          }

          return {
            method,
            headers,
            body: isFormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
            signal: options?.signal,
          };
        },
        options?.signal,
      );
    },
    [execute],
  );

  // Mirrors `request` above for multipart/form-data — no explicit Content-Type (browser sets boundary)
  const requestMultipart = useCallback(
    <T>(path: string, body: FormData, options?: RequestOptions): Promise<T> =>
      execute<T>(
        path,
        token => {
          const headers = new Headers({
            ...options?.headers,
            'tenant-id': DEFAULT_TENANT,
          });

          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
          }

          return { method: 'POST', headers, body, signal: options?.signal };
        },
        options?.signal,
      ),
    [execute],
  );

  const client = useMemo(
    () => ({
      get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
      post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
        request<T>('POST', path, body, opts),
      patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
        request<T>('PATCH', path, body, opts),
      put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
        request<T>('PUT', path, body, opts),
      delete: <T = void>(path: string, opts?: RequestOptions) =>
        request<T>('DELETE', path, undefined, opts),
      postMultipart: <T>(path: string, body: FormData, opts?: RequestOptions) =>
        requestMultipart<T>(path, body, opts),
    }),
    [request, requestMultipart],
  );

  return [client, isLoading];
}
